/** @jsx jsx */
import React, { memo, type PropsWithChildren } from 'react';
import { cssMap, styled, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { SIMPLE } from '@atlassian/jira-platform-inline-card-create/src/common/constants.tsx';
import JiraInlineCardCreate from '@atlassian/jira-platform-inline-card-create/src/ui/main.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import type { InlineCardCreateProps } from './types.tsx';

const styles = cssMap({
	iccWrapper: {
		position: 'sticky',
		bottom: 0,
		paddingTop: token('space.025'),
		paddingRight: 0,
		paddingBottom: token('space.025'),
		paddingLeft: 0,
		borderRadius: '0 0 6px 6px',
		backgroundColor: token('elevation.surface.sunken'),
		marginTop: 0,
		marginRight: token('space.075'),
		marginBottom: 0,
		marginLeft: token('space.075'),
	},
	themed: {
		backgroundColor: 'transparent',
	},
});

const ConditionalIccWrapper = ({
	isVisuallyRefreshed,
	children,
}: PropsWithChildren<{ isVisuallyRefreshed: boolean }>) => {
	const { isSoftwareFullTheming } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	if (isVisuallyRefreshed) {
		return (
			<div
				data-testid="software-backlog-panel.common.ui.inline-card-create.jira-inline-card-create"
				css={[styles.iccWrapper, isSoftwareFullTheming && styles.themed]}
			>
				{children}
			</div>
		);
	}

	return (
		<IccWrapper data-testid="software-backlog-panel.common.ui.inline-card-create.jira-inline-card-create">
			{children}
		</IccWrapper>
	);
};

const InlineCardCreate = ({
	id,
	triggerAppearance,
	triggerLabel,
	placeholder,
	onCreate,
}: InlineCardCreateProps) => (
	<ConditionalIccWrapper isVisuallyRefreshed={isVisualRefreshEnabled()}>
		<JiraInlineCardCreate
			cardTypes={[{ id, name: '', iconUrl: '' }]}
			appearance={SIMPLE}
			triggerAppearance={triggerAppearance}
			triggerLabel={triggerLabel}
			placeholderText={placeholder}
			onCreate={onCreate}
		/>
	</ConditionalIccWrapper>
);

export default memo<InlineCardCreateProps>(InlineCardCreate);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IccWrapper = styled.div({
	position: 'sticky',
	bottom: 0,

	paddingTop: token('space.025'),
	paddingRight: 0,
	paddingBottom: token('space.025'),
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `0 0 ${borderRadius * 2}px ${borderRadius * 2}px`,

	backgroundColor: token('elevation.surface.sunken'),
});
